import COS from 'cos-js-sdk-v5'
import { algo, lib } from 'crypto-js'
import FileSaver from 'file-saver'
import mime from 'mime'

export type TokenData = {
  TmpSecretId: string
  TmpSecretKey: string
  Token: string
  startTime: number
  expiredTime: number
  Bucket: string
  Region: string
}

export type TypeFileResult = {
  key: string
  fileHash: string
  fileSize: number
  fileType: string
  fileName: string
}

export abstract class FileHelper {
  private _cacheCos: Record<'upload' | 'download', COS | null> = {
    upload: null,
    download: null,
  }

  private _tokenCache: Record<'upload' | 'download', TokenData | null> = {
    upload: null,
    download: null,
  }

  private _tokenRequestLock: Record<'upload' | 'download', Promise<TokenData | null> | null> = {
    upload: null,
    download: null,
  }

  public region: string = 'ap-guangzhou'
  public bucket: string = 'byy-1312430304'

  constructor(public prefix: string, bucket?: string, region?: string) {
    this.bucket = bucket || this.bucket
    this.region = region || this.region
  }

  /** 获取文件MD5 */
  static getFileMd5(file: File) {
    return new Promise<string | undefined>((resolve) => {
      try {
        const chunkSize = 2097152 // 2MB
        const chunks = Math.ceil(file.size / chunkSize)
        let currentChunk = 0
        const spark = algo.MD5.create()
        const reader = new FileReader()

        reader.onload = function (e) {
          const fileContent = e.target?.result
          if (fileContent) {
            spark.update(lib.WordArray.create(fileContent as any))
            currentChunk++

            if (currentChunk < chunks) {
              loadNext()
            } else {
              const hash = spark.finalize().toString()
              resolve(hash)
            }
          } else {
            resolve(undefined)
          }
        }

        reader.onerror = function () {
          resolve(undefined)
        }

        const loadNext = () => {
          const start = currentChunk * chunkSize
          const end = start + chunkSize >= file.size ? file.size : start + chunkSize
          reader.readAsArrayBuffer(file.slice(start, end))
        }

        loadNext()
      } catch {
        resolve(undefined)
      }
    })
  }

  // 将图片转换为png
  static imgToPng(blob: Blob) {
    return new Promise<Blob | null>((resolve, reject) => {
      let img = document.createElement('img')
      img.onload = () => {
        let canvas = document.createElement('canvas')
        let ctx = canvas.getContext('2d')
        if (ctx) {
          canvas.width = img.width
          canvas.height = img.height
          ctx.drawImage(img, 0, 0, img.width, img.height)
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to create blob'))
            }
          }, 'image/png')
        } else {
          reject(new Error('Failed to get canvas context'))
        }
      }
      img.onerror = reject
      img.src = URL.createObjectURL(blob)
    })
  }

  /** 获取cos实例 */
  private getCos(type: 'upload' | 'download', key?: string): COS {
    if (!this._cacheCos[type]) {
      this._cacheCos[type] = new COS({
        getAuthorization: async (options, callback) => {
          const cachedToken = this._tokenCache[type]
          // 提前3分钟更新，避免过期
          if (cachedToken && Date.now() < cachedToken.expiredTime * 1000 - 3 * 1000 * 60) {
            callback({
              TmpSecretId: cachedToken.TmpSecretId,
              TmpSecretKey: cachedToken.TmpSecretKey,
              SecurityToken: cachedToken.Token,
              StartTime: cachedToken.startTime,
              ExpiredTime: cachedToken.expiredTime,
            })
            return
          }

          // 如果没有缓存或已过期，检查是否有正在进行的请求
          if (this._tokenRequestLock[type]) {
            // 等待当前请求完成
            const data = await this._tokenRequestLock[type]
            if (data) {
              callback({
                TmpSecretId: data.TmpSecretId,
                TmpSecretKey: data.TmpSecretKey,
                SecurityToken: data.Token,
                StartTime: data.startTime,
                ExpiredTime: data.expiredTime,
              })
              return
            }
          }

          // 创建新的请求锁
          this._tokenRequestLock[type] = (async () => {
            try {
              const data =
                type === 'upload' ? await this.getUploadToken() : await this.getDownloadToken('*')
              if (!data) {
                console.error('获取 COS 临时密钥失败')
                return null
              }

              // 更新缓存
              this._tokenCache[type] = data
              return data
            } finally {
              // 请求完成后释放锁
              this._tokenRequestLock[type] = null
            }
          })()

          // 等待请求完成并回调
          const data = await this._tokenRequestLock[type]
          if (data) {
            callback({
              TmpSecretId: data.TmpSecretId,
              TmpSecretKey: data.TmpSecretKey,
              SecurityToken: data.Token,
              StartTime: data.startTime,
              ExpiredTime: data.expiredTime,
            })
          }
        },
      })
    }
    return this._cacheCos[type]
  }

  /** 获取文件 hash、ext */
  static async getFileInfo(file: File) {
    const fileMd5 = await FileHelper.getFileMd5(file)
    let extension = mime.getExtension(file.type) // 根据文件类型获取实际后缀，获取不到时在取文件名里
    if (!extension) extension = file.name?.split('.')[1]
    const ext = extension ? `.${extension}` : ''
    return { fileMd5, ext, extension }
  }

  /** 上传文件 */
  async upload(file: File): Promise<TypeFileResult | undefined> {
    const cos = this.getCos('upload')
    if (!cos) return

    const { fileMd5, ext, extension } = await FileHelper.getFileInfo(file)
    if (!fileMd5) return

    const Key = `${this.prefix}/${fileMd5}${ext}`

    const result = await cos.uploadFile({
      Bucket: this.bucket,
      Region: this.region,
      Key,
      Body: file,
    })

    if (result.statusCode !== 200) return

    return {
      key: Key,
      fileHash: fileMd5,
      fileSize: file.size,
      fileType: extension,
      fileName: file.name,
    }
  }

  /** 批量上传文件 */
  async batchUpload(
    files: File[],
    onFileFinish: (data: TypeFileResult & { success: boolean }) => void
  ) {
    const cos = this.getCos('upload')
    if (!cos) return

    return await Promise.all(
      files.map(async (item) => {
        const { fileMd5, ext, extension } = await FileHelper.getFileInfo(item)
        if (!fileMd5) return { success: false }
        const Key = `${this.prefix}/${fileMd5}${ext}`

        const fileInfo: TypeFileResult = {
          key: Key,
          fileHash: fileMd5,
          fileSize: item.size,
          fileType: extension,
          fileName: item.name,
        }

        const result = await cos.uploadFile({
          Bucket: this.bucket,
          Region: this.region,
          Key,
          Body: item,
        })

        return onFileFinish({
          ...fileInfo,
          success: result.statusCode === 200,
        })
      })
    )
  }

  /** 下载文件 */
  async download(key: string, filename?: string) {
    const downloadUrl = await this.getDownloadUrl(key, filename)
    if (!downloadUrl) return
    FileSaver.saveAs(downloadUrl, filename)
  }

  /** 获取下载地址 */
  async getDownloadUrl(key: string, filename?: string) {
    return new Promise<string | undefined>((resolve, reject) => {
      const cos = this.getCos('download', key)
      if (!cos) return resolve(undefined)

      cos.getObjectUrl(
        {
          Bucket: this.bucket,
          Region: this.region,
          Key: key,
          Sign: true,
        },
        function (err, data) {
          if (err) return console.log(err)
          var downloadUrl =
            data.Url +
            (data.Url.indexOf('?') > -1 ? '&' : '?') +
            'response-content-disposition=attachment' // 补充强制下载的参数
          downloadUrl += `;filename="${filename}"`
          resolve(downloadUrl)
        }
      )
    })
  }

  /** 获取文件内容 */
  async getFileContent(
    key: string,
    type: 'blob' | 'text' = 'blob',
    onProgress?: (progressData: any) => void
  ): Promise<Blob | string | undefined> {
    const cos = this.getCos('download', key)
    if (!cos) return

    const result = await cos.getObject({
      Bucket: this.bucket,
      Region: this.region,
      Key: key,
      DataType: type, // 非必须
      onProgress,
    })

    return result?.Body as Blob
  }

  /** 复制图片 */
  async copyImgToClipboard(src: string) {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const isSvg = /\.(svg)$/.test(src)

        this.getFileContent(src, isSvg ? 'text' : 'blob')
          .then(async (content) => {
            if (!content) return reject('复制失败，请下载使用')
            if (content instanceof Blob) {
              const blob = await FileHelper.imgToPng(content)
              if (!blob) return reject('复制失败，请下载使用')

              const data = [new ClipboardItem({ [blob.type]: blob })]
              return navigator.clipboard.write(data)
            } else {
              return navigator.clipboard.writeText(content)
            }
          })
          .then(() => {
            console.log('copyImgToClipboard success')
            resolve()
          })
          .catch((error) => {
            reject(error)
          })
      } catch (error) {
        console.log(error)
        reject(error)
      }
    })
  }

  /** 获取上传令牌 */
  abstract getUploadToken(): Promise<TokenData | null>

  /** 获取下载令牌 */
  abstract getDownloadToken(key: string): Promise<TokenData | null>
}
